import type { FC, ReactNode } from 'react'
import type { DetailSection } from '@/types/content'

import { CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DetailSectionsProps {
    sections: DetailSection[]
    label?: string
    className?: string
}

const DetailSections: FC<DetailSectionsProps> = ({ sections = [], label = "The details", className }): ReactNode => {
    if (!sections.length) return null

    return (
        <section aria-label={label} className={cn('mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8', className)}>
            <p className='text-sm font-bold uppercase tracking-[0.14em] text-primary' data-reveal>
                {label}
            </p>

            <div className='mt-4 divide-y divide-border border-t border-border'>
                {sections.map(section => (
                    <article key={section.heading} className='grid gap-6 py-10 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)] lg:gap-14' data-reveal>
                        <h2 className='text-2xl font-bold sm:text-3xl'>
                            {section.heading}
                        </h2>

                        <div className='max-w-[68ch] text-lg leading-relaxed'>
                            {section.paragraphs?.map(paragraph => (
                                <p key={paragraph.slice(0, 40)} className='mb-4 last:mb-0'>
                                    {paragraph}
                                </p>
                            ))}

                            {section.pairs && (
                                <dl className='mt-2 grid gap-5 sm:grid-cols-2'>
                                    {section.pairs.map(pair => (
                                        <div key={pair.term} className='rounded-2xl bg-white p-5 shadow-sm'>
                                            <dt className='text-xl font-bold text-navy'>
                                                {pair.term}
                                            </dt>

                                            <dd className='mt-2 text-base leading-relaxed'>
                                                {pair.description}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}

                            {section.bullets && (
                                <ul className='mt-4 space-y-3'>
                                    {section.bullets.map(bullet => (
                                        <li key={bullet} className='flex items-start gap-3'>
                                            <CheckCircle2 className='mt-1 size-5 shrink-0 text-primary' aria-hidden='true' />
                                            <span>{bullet}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </article>
                ))}
            </div>
        </section>
    )
}

export default DetailSections
